import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Folder, Grid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ArtworkCard from '@/components/ArtworkCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NSFWGalleryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSeries = searchParams.get('series');
  
  const [ageVerified, setAgeVerified] = useState(false);
  const [showModal, setShowModal] = useState(true);
  const [artworks, setArtworks] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [currentSeriesDescription, setCurrentSeriesDescription] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user has already verified age
    const verified = localStorage.getItem('age_verified_nsfw');
    if (verified === 'true') {
      setAgeVerified(true);
      setShowModal(false);
      fetchArtworks();
      fetchSeries();
    } else {
      setLoading(false);
    }
  }, [selectedSeries]);

  // Update current series description when series changes
  useEffect(() => {
    if (selectedSeries && seriesList.length > 0) {
      const series = seriesList.find(s => s.name === selectedSeries);
      setCurrentSeriesDescription(series?.description || '');
    } else {
      setCurrentSeriesDescription('');
    }
  }, [selectedSeries, seriesList]);

  const fetchArtworks = async () => {
    try {
      // Only fetch artworks if a series is selected or "all" is explicitly chosen
      if (!selectedSeries && searchParams.get('view') !== 'all') {
        setArtworks([]);
        setLoading(false);
        return;
      }
      
      // Fetch NSFW artworks, filtered by series if selected
      const params = { gallery_type: 'nsfw' };
      if (selectedSeries) {
        params.series_name = selectedSeries;
      }
      const response = await axios.get(`${API}/artworks`, { params });
      setArtworks(response.data);
    } catch (error) {
      console.error('Failed to fetch artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeries = async () => {
    try {
      const response = await axios.get(`${API}/series?gallery_type=nsfw`);
      // Response now includes preview_images, artwork_count, and description
      setSeriesList(response.data);
    } catch (error) {
      console.error('Failed to fetch series:', error);
    }
  };

  const handleVerifyAge = () => {
    localStorage.setItem('age_verified_nsfw', 'true');
    setAgeVerified(true);
    setShowModal(false);
    fetchArtworks();
    fetchSeries();
  };

  const handleDeclineAge = () => {
    navigate('/');
  };

  // Component for series card with preview images
  const SeriesCard = ({ series, onClick }) => {
    const previewImages = series.preview_images || [];
    
    return (
      <button
        onClick={onClick}
        className="group relative overflow-hidden bg-card border-2 border-border hover:border-accent transition-all duration-300 rounded-lg text-left w-full"
        data-testid={`series-card-${series.name}`}
      >
        {/* Preview Images Grid */}
        <div className="aspect-square relative overflow-hidden bg-secondary">
          {previewImages.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center">
              <Folder className="w-16 h-16 text-muted-foreground" />
            </div>
          ) : previewImages.length === 1 ? (
            <img 
              src={previewImages[0]} 
              alt={series.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : previewImages.length === 2 ? (
            <div className="grid grid-cols-2 h-full">
              {previewImages.map((img, idx) => (
                <img 
                  key={`${series.name}-preview-${idx}`} 
                  src={img} 
                  alt={`${series.name} preview ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ))}
            </div>
          ) : previewImages.length === 3 ? (
            <div className="grid grid-cols-2 h-full">
              <img 
                src={previewImages[0]} 
                alt={`${series.name} preview 1`}
                className="w-full h-full object-cover row-span-2 group-hover:scale-105 transition-transform duration-500"
              />
              <div className="grid grid-rows-2">
                {previewImages.slice(1, 3).map((img, idx) => (
                  <img 
                    key={`${series.name}-preview-${idx + 1}`} 
                    src={img} 
                    alt={`${series.name} preview ${idx + 2}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 grid-rows-2 h-full">
              {previewImages.slice(0, 4).map((img, idx) => (
                <img 
                  key={`${series.name}-preview-${idx}`} 
                  src={img} 
                  alt={`${series.name} preview ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ))}
            </div>
          )}
          
          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
        </div>
        
        {/* Series Info */}
        <div className="p-4">
          <h3 className="text-xl font-serif mb-1 group-hover:text-accent transition-colors">
            {series.name}
          </h3>
          <p className="text-sm text-muted-foreground">
            {series.artwork_count} {series.artwork_count === 1 ? 'artwork' : 'artworks'}
          </p>
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="nsfw-gallery-page">
      {/* Age Verification Modal */}
      <AnimatePresence>
        {showModal && !ageVerified && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
            data-testid="age-verification-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border rounded-lg p-8 md:p-12 max-w-2xl mx-4 text-center"
            >
              <div className="flex justify-center mb-6">
                <AlertTriangle className="h-16 w-16 text-accent" />
              </div>
              
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-6" data-testid="age-verification-title">
                Adult Content Warning
              </h2>
              
              <div className="text-base md:text-lg leading-relaxed text-muted-foreground mb-8 space-y-4">
                <p>
                  This gallery contains artistic works featuring nudity and mature themes 
                  intended for adult audiences only.
                </p>
                <p className="font-medium text-foreground">
                  You must be 18 years or older to view this content.
                </p>
                <p className="text-sm">
                  By clicking "I'm 18 or Older" below, you certify that you are an adult 
                  and agree to view content that may contain nudity and mature artistic expression.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={handleVerifyAge}
                  className="rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                  data-testid="age-verify-yes-btn"
                >
                  I'm 18 or Older
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleDeclineAge}
                  className="rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                  data-testid="age-verify-no-btn"
                >
                  I'm Under 18
                </Button>
              </div>

              <p className="text-xs text-muted-foreground mt-8">
                This verification is required by law to protect minors from accessing adult content.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery Content (only shows after age verification) */}
      {ageVerified && (
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-none mb-6" data-testid="nsfw-gallery-title">
              NSFW Collection
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-4">
              A collection exploring the human form and intimate themes
            </p>
            <p className="text-sm text-muted-foreground">
              This gallery contains artistic nudity and mature content
            </p>
          </div>

          {/* Show Series Selection if no series is selected and not viewing all */}
          {!selectedSeries && searchParams.get('view') !== 'all' && seriesList.length > 0 ? (
            <div>
              <h2 className="text-2xl md:text-3xl font-serif text-center mb-8">
                Browse by Series
              </h2>
              <p className="text-center text-muted-foreground mb-12">
                Select a series to explore, or view all artworks
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {/* View All Card */}
                <button
                  onClick={() => setSearchParams({ view: 'all' })}
                  className="group relative overflow-hidden bg-card border-2 border-border hover:border-accent transition-all duration-300 rounded-lg text-left w-full"
                  data-testid="view-all-series-card"
                >
                  <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-accent/10 to-accent/5 flex items-center justify-center">
                    <Grid className="w-20 h-20 text-accent opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-xl font-serif mb-1 group-hover:text-accent transition-colors">
                      View All
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Browse all artworks in this collection
                    </p>
                  </div>
                </button>

                {/* Series Cards with Preview Images */}
                {seriesList.map((series) => (
                  <SeriesCard
                    key={series.name}
                    series={series}
                    onClick={() => setSearchParams({ series: series.name })}
                  />
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Back button and series filter when viewing artworks */}
              <div className="mb-12">
                <button
                  onClick={() => setSearchParams({})}
                  className="mb-6 text-sm uppercase tracking-widest text-muted-foreground hover:text-accent transition-colors flex items-center gap-2"
                  data-testid="back-to-series-btn"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Series
                </button>

                {/* Series Title and Description */}
                {selectedSeries && (
                  <div className="text-center mb-10">
                    <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-4">
                      {selectedSeries}
                    </h2>
                    {currentSeriesDescription && (
                      <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto whitespace-pre-line">
                        {currentSeriesDescription}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex justify-center gap-4 flex-wrap">
                  <Button
                    variant={searchParams.get('view') === 'all' ? 'default' : 'outline'}
                    onClick={() => setSearchParams({ view: 'all' })}
                    className="rounded-full px-6 uppercase tracking-widest text-xs"
                    data-testid="filter-all-series-btn"
                  >
                    ALL
                  </Button>
                  {seriesList.map((series) => (
                    <Button
                      key={series.name}
                      variant={selectedSeries === series.name ? 'default' : 'outline'}
                      onClick={() => setSearchParams({ series: series.name })}
                      className="rounded-full px-6 uppercase tracking-widest text-xs"
                      data-testid={`filter-series-${series.name}`}
                    >
                      {series.name}
                    </Button>
                  ))}
                </div>
              </div>

              {loading ? (
                <div className="text-center text-muted-foreground py-20">Loading artworks...</div>
              ) : artworks.length === 0 ? (
                <div className="text-center text-muted-foreground py-20" data-testid="no-nsfw-artworks">
                  No artworks available in this series.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16" data-testid="nsfw-artworks-grid">
                  {artworks.map((artwork) => (
                    <ArtworkCard key={artwork.id} artwork={artwork} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
